import {EnumThemes} from 'apps/BWW_TRIVIA_WEB/modules/utils/constants/enums';
import {filter, find, flatMap, get, includes, isNull, isNumber, last, map, memoize, pickBy, size, sumBy} from 'lodash';
import {
	getActualContest,
	getActualContestRound,
	getAllRounds,
	getContestByRoundIDSelector,
	getLastGameContest,
	getRoundByIDSelector
} from 'modules/selectors/contests_trivia';
import {
	AnswerEventTypes,
	IGlobalStore,
	IQuestionResultAction,
	ITriviaQuestion,
	ITriviaRound,
	Optional
} from 'modules/types';
import {EnumAnswerStates, EnumInGameState, EnumTriviaStatesOfGame, EnumTriviaStatusType} from 'modules/utils/enums';
import {getTimeToComplete} from 'modules/utils/trivia';
import {createSelector} from 'reselect';


export const getTriviaStore = (store: IGlobalStore) => store.trivia;

export const isLoading = createSelector(
	[getTriviaStore],
	(trivia) => trivia.is_loading
);

export const isShowWarning = createSelector(
	[getTriviaStore],
	(trivia) => trivia.is_show_warning
);

export const isShowFanFact = createSelector(
	[getTriviaStore],
	(trivia) => trivia.is_show_fan_fact
);

export const isShowPointsBadge = createSelector(
	[getTriviaStore],
	(trivia) => trivia.is_show_points_badge
);

export const isShowPointsWagerBadge = createSelector(
	[getTriviaStore],
	(trivia) => trivia.is_show_points_wager_badge
);

export const isShowSwipeItBonusPoints = createSelector(
	[getTriviaStore],
	(trivia) => trivia.is_show_bonus_points_swipe_it
);

export const getTriviaQuestions = createSelector(
	[getTriviaStore],
	(trivia): ITriviaQuestion[] => trivia.questions
);

export const getTriviaQuestionsByRoundID = createSelector(
	[getTriviaQuestions],
	(questions): (round_id: number) => ITriviaQuestion[] =>
		(round_id) => filter(questions, {round_id})
);

export const getTriviaQuestionToDisplay = createSelector(
	[getTriviaStore],
	(trivia) => trivia.display_question
);

export const getTriviaSetToDisplay = createSelector(
	[getTriviaStore],
	(trivia) => trivia.display_set_questions
);

export const getTriviaUserAnswerSelector = createSelector(
	[getTriviaStore],
	(trivia) =>
		memoize((question_id: number) => trivia.user_answers[question_id])
);

export const getTriviaAnswerSelector = createSelector(
	[getTriviaStore],
	(trivia) => (question_id: number) => trivia.answers[question_id]
);

export const getTriviaQuestionsStats = createSelector(
	[getActualContestRound, getTriviaQuestionToDisplay],
	(round: ITriviaRound | undefined, question: ITriviaQuestion) => {
		return {
			round_number: get(round, 'position', 0),
			number_questions: get(round, 'number_questions', 0),
			question_number: get(question, 'position', 0),
		}
	}
);

export const getTriviaQuestionResultsState = createSelector(
	[getTriviaStore],
	(trivia) => trivia.questions_result
);

export const getTriviaQuestionResultsByID = createSelector(
	[getTriviaStore],
	(trivia) => trivia.questions_result.questions
);

type TGetTriviaQuestionResultsByRoundID = (round_id?: number) => Record<string, IQuestionResultAction>
export const getTriviaQuestionResultsByRoundID = createSelector(
	[getTriviaStore],
	(trivia): TGetTriviaQuestionResultsByRoundID => (round_id: number | undefined = 0) => {
		return pickBy(trivia.questions_result.questions, {round_id})
	}
);

export const getTriviaQuestionResultSelector = createSelector(
	[getTriviaStore],
	(trivia) => (question_id: number) => {
		return trivia.questions_result.questions[question_id] || {
			answer_state: EnumAnswerStates.unknown
		}
	}
);

export const getTriviaQuestionsSelector = createSelector(
	[getTriviaStore],
	(trivia) => {
		return (question_id: number) => {
			return find(trivia.questions, {id: question_id})
		}
	}
);

export const getStateOfGame = createSelector(
	[getTriviaStore],
	(trivia) => trivia.state_of_game
);

export const getStateInGame = createSelector(
	[getTriviaStore],
	(trivia) => trivia.state_in_game
);

export const getStateAfterRound = createSelector(
	[getTriviaStore],
	(trivia) => trivia.state_after_round
);

export const getBonusPoints = createSelector(
	[getTriviaStore],
	(trivia) => trivia.bonus_points
);


export const getUserPoints = createSelector(
	[getTriviaQuestionResultsByRoundID, getActualContestRound, getBonusPoints],
	(roundSelector: any, round: ITriviaRound | undefined, bonus_points: number) => {
		const questions = roundSelector(round?.id);
		const questionsWithPoints = filter(questions, (q_item: IQuestionResultAction) => {
			return !isNull(q_item.points) && isNumber(q_item.points)
		});

		if (!questionsWithPoints.length) {
			return null
		}

		return sumBy(questionsWithPoints, 'points') + bonus_points
	}
);

export const getUserWagerSelector = createSelector(
	[getTriviaStore],
	(trivia) => {
		return trivia.wager
	}
);

export const getLiveWagerSelector = createSelector(
	[getTriviaStore],
	(trivia) => {
		return trivia.questions_result.wager
	}
);

export const getContestPointsFromWagersSelector = createSelector(
	[getTriviaStore],
	(trivia) => {
		return trivia.wager.contest_points
	}
);

export const getPlayingContestID = createSelector(
	[getTriviaStore],
	(trivia) => {
		return trivia.playing_contest_id
	}
);


export const getTimeToAnswer = createSelector(
	[getActualContestRound, getTriviaQuestionToDisplay],
	(round: ITriviaRound | undefined, current_question: ITriviaQuestion | undefined,) => {

		const currentTime = new Date().getTime();
		const questionTime = new Date(current_question?.end_date_time || '').getTime();
		const diffTime = questionTime - currentTime;
		const time = diffTime / 1000;
		if (time <= 0) {
			return 1
		}

		const round_time = round?.time_to_answer || 20;

		if (round_time < time) {
			return round_time
		}

		return diffTime / 1000
	}
);

export const getTimeToAnswerSwipeIt = createSelector(
	[getActualContestRound, getTriviaSetToDisplay],
	(round: ITriviaRound | null = null, questions: ITriviaQuestion[]) => () => {
		const last_question = last(questions);
		const time_to_answer = round?.time_to_answer || 12;

		if (last_question && last_question.end_date_time) {
			return {
				time_to_answer,
				time_to_complete: getTimeToComplete(last_question.end_date_time)
			}
		}

		const time_to_complete = time_to_answer * questions.length;
		return {time_to_answer, time_to_complete}
	}
);

export const getTriviaFooterRightSideState = createSelector(
	[getTriviaStore],
	(store) => store.footer_state.right_side
);

export const getTriviaFooterTosState = createSelector(
	[getTriviaStore],
	(store) => store.footer_state.tos
);

export const getTriviaFooterMsgState = createSelector(
	[getTriviaStore],
	(store) => store.footer_state.show_footer_msg
);

export const getUserScoreByRoundIDSelector = createSelector(
	[getTriviaQuestionResultsByRoundID],
	(questionsSelector) => memoize((round_id: number) => {
		const questions = questionsSelector(round_id);

		return size(filter(questions, {
			answer_state: EnumAnswerStates.correct
		}))
	})
);

export const getTriviaQuestionByIDSelector = createSelector(
	[getAllRounds, getRoundByIDSelector, getContestByRoundIDSelector],
	(rounds, roundSelector, contestSelector) => (id: number) => {
		const questionsArray = flatMap(rounds, 'questions');

		const question: Optional<ITriviaQuestion> = find(questionsArray, {
			id
		});

		if (!question) {
			return null;
		}

		const contest = contestSelector(question.round_id);
		const round = roundSelector(question.round_id);
		const time_to_answer = (round?.time_to_answer || 10) * 1000;

		return {
			contest,
			round,
			question: {
				...question,
				start_date_time: new Date().getTime(),
				status: EnumTriviaStatusType.live,
				end_date_time: new Date().getTime() + time_to_answer
			},
			answer: map<any, AnswerEventTypes>(question.answer_choices, (choice) => {
				return {
					...choice,
					question_id: question.id,
				}
			}),
			time_to_answer
		}
	}
);


/**
 * Returns live or first scheduled contest for most part of game but for end (post game) - it returns last completed
 */
export const getActualGameContest = createSelector(
	[getStateOfGame, getActualContest, getLastGameContest],
	(state_in_game, actual_contest, last_game_contest) => {
		return state_in_game === EnumTriviaStatesOfGame.end_game ? last_game_contest : actual_contest;
	}
)

/**
 * Checks if actual GAME contest is herds
 */
export const isHerdActualContest = createSelector(
	[getActualGameContest],
	(contest) => {
		return Boolean(contest?.is_herd)
	}
);

export const getQuestionTheme = createSelector(
	[getTriviaQuestionToDisplay, getRoundByIDSelector, getStateInGame],
	(question, roundSelector, state) => {
		const isQuestion = !includes([EnumInGameState.unknown, EnumInGameState.fun_fact], state);
		const round = roundSelector(question.round_id);

		if (!isQuestion) {
			return EnumThemes.None
		}

		if (round?.name === 'finals') {
			return EnumThemes.Finals
		}

		if (round?.name === 'blazin_round') {
			return EnumThemes.Blazin
		}

		if (round?.name === 'michelob_ultra_dulce_vida') {
			return EnumThemes.Michelob
		}

		return EnumThemes.None
	}
)
